import {
  AgentType,
  ChatDisplayMode,
  ChatPlacementPosition,
} from '@kanbu/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import { useFieldState } from 'informed';
import { memo } from 'react';

import { Card } from '@/components/card/Card';
import { optionsFromEnum } from '@/components/form/formUtils';
import { SchemaFields } from '@/components/form/SchemaFields';
import { useFieldsSchema } from '@/components/form/useFieldsSchema';
import { useEnumTranslations } from '@/hooks';

export const ChatPlacementFields = memo(function ChatPlacementFields() {
  const { t } = useLingui();
  const typeFieldState = useFieldState('agentType');
  const enumTranslations = useEnumTranslations();

  const { schema: detailsSchema } = useFieldsSchema(
    [
      {
        name: 'chatbotConfig.displayMode',
        label: t`Display mode`,
        type: 'select',
        options: optionsFromEnum(
          ChatDisplayMode,
          key => enumTranslations.chatDisplayMode[key],
        ),
        defaultValue: ChatDisplayMode.Widget,
        showOptional: false,
        uiProps: {
          tooltip: (
            <div className='flex max-w-80 flex-col gap-2'>
              <h4 className='text-base font-bold'>{t`Display mode`}</h4>
              <p className='text-sm'>
                <Trans>
                  Widget keeps the corner dialog when open. Full page opens a
                  chat with a conversation sidebar. Placement still applies to
                  the bubble and callouts while closed; use Open chat
                  automatically if the chat should expand on page load.
                </Trans>
              </p>
            </div>
          ),
        },
      },
      {
        name: 'chatbotConfig.placement.position',
        label: t`Position`,
        type: 'select',
        options: optionsFromEnum(
          ChatPlacementPosition,
          key => enumTranslations.chatPlacementPosition[key],
        ),
        defaultValue: ChatPlacementPosition.Right,
        showOptional: false,
      },
      {
        name: 'chatbotConfig.placement.offsetX',
        type: 'number',
        label: t`Offset X`,
        defaultValue: 0,
        showOptional: false,
        uiProps: {
          addonAfter: 'px',
        },
      },
      {
        name: 'chatbotConfig.placement.offsetY',
        type: 'number',
        label: t`Offset Y`,
        defaultValue: 0,
        showOptional: false,
        uiProps: {
          addonAfter: 'px',
        },
      },
    ],
    [
      t,
      enumTranslations.chatDisplayMode,
      enumTranslations.chatPlacementPosition,
    ],
  );

  // These fields should be visible only if the agent type is Chat
  if (typeFieldState.value !== AgentType.Chat) {
    return null;
  }

  return (
    <Card title={t`Placement`}>
      <SchemaFields schema={detailsSchema} />
    </Card>
  );
});
